import { Inject, Injectable, InjectionToken } from '@angular/core';

import { environment } from '../../../environments/environment';
import { ProfileService } from '../../profile/profile.service';
import {
  buildChunkUrl,
  canRecord,
  parseQaCard,
  sanitizeEvent,
  sanitizeNetworkRequest,
  stripUrl,
  type RecordedRequest,
  type SessionReplayConfig,
} from './session-replay-policy';

// Records every browsing session with rrweb and uploads gzipped chunks to the
// session replay intake (backend-serverless/deploy/session-replay/stack.yaml).
// See docs/session-replay.md.

type ReplayEvent = { type: number; timestamp: number; data?: unknown };
type StopRecording = () => void;
export type StartRecording = (onEvent: (event: ReplayEvent) => void, ingestUrl: string) => StopRecording;
export type RecorderLoader = () => Promise<StartRecording>;

export interface SessionReplayRuntime {
  location: Pick<Location, 'hostname' | 'search' | 'href'>;
  storage: Storage;
  randomUUID: () => string;
  today: () => string;
  fetch: typeof fetch;
  compress: (text: string) => Promise<Blob>;
  setInterval: (fn: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
  onPageHide: (fn: () => void) => () => void;
}

const FLUSH_INTERVAL_MS = 30_000;
// Upload early once this much raw JSON is buffered; it gzips to well under the intake's 1 MB cap.
const FLUSH_AT_CHARS = 2_000_000;
// Browsers refuse keepalive requests over 64 KB.
const KEEPALIVE_MAX_BYTES = 60_000;
const FULL_SNAPSHOT_EVENT = 2;
const SESSION_KEY = 'coursebuilder.replay.session';
const QA_CARD_KEY = 'coursebuilder.replay.qaCard';

interface StoredSession {
  id: string;
  startDate: string;
  seq: number;
}

const gzip = async (text: string): Promise<Blob> => {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
};

const loadRrweb: RecorderLoader = async () => {
  const [{ record }, { getRecordConsolePlugin }, { getRecordNetworkPlugin }] = await Promise.all([
    import('@rrweb/record'),
    import('@rrweb/rrweb-plugin-console-record'),
    import('@rrweb/rrweb-plugin-network-record'),
  ]);
  return (onEvent, ingestUrl) => record({
    emit: (event) => onEvent(event as ReplayEvent),
    maskAllInputs: true,
    recordCanvas: false,
    collectFonts: false,
    inlineImages: false,
    plugins: [
      getRecordConsolePlugin({ level: ['error', 'warn'], stringifyOptions: { stringLengthLimit: 500, numOfKeysLimit: 50, depthOfLimit: 4 } }),
      getRecordNetworkPlugin({
        initiatorTypes: ['fetch', 'xmlhttprequest'],
        recordHeaders: false,
        recordBody: false,
        transformRequestFn: (request) =>
          sanitizeNetworkRequest(request as RecordedRequest, ingestUrl) as typeof request | undefined,
      }),
    ],
  }) ?? (() => undefined);
};

export const SESSION_REPLAY_CONFIG = new InjectionToken<SessionReplayConfig>('SESSION_REPLAY_CONFIG', {
  factory: () => environment.sessionReplay,
});

export const SESSION_REPLAY_RUNTIME = new InjectionToken<SessionReplayRuntime>('SESSION_REPLAY_RUNTIME', {
  factory: () => ({
    location: window.location,
    storage: window.sessionStorage,
    randomUUID: () => window.crypto.randomUUID(),
    today: () => new Date().toISOString().slice(0, 10),
    fetch: (...args) => window.fetch(...args),
    compress: gzip,
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),
    onPageHide: (fn) => {
      const onVisibility = () => { if (document.visibilityState === 'hidden') fn(); };
      window.addEventListener('pagehide', fn);
      document.addEventListener('visibilitychange', onVisibility);
      return () => {
        window.removeEventListener('pagehide', fn);
        document.removeEventListener('visibilitychange', onVisibility);
      };
    },
  }),
});

export const SESSION_REPLAY_RECORDER_LOADER = new InjectionToken<RecorderLoader>('SESSION_REPLAY_RECORDER_LOADER', {
  factory: () => loadRrweb,
});

@Injectable({ providedIn: 'root' })
export class SessionReplayService {
  private buffer: ReplayEvent[] = [];
  private bufferedChars = 0;
  private session?: StoredSession;
  private qaCard: string | null = null;
  private stopRecording?: StopRecording;
  private timer?: number;
  private removePageHide?: () => void;

  constructor(
    private readonly profileService: ProfileService,
    @Inject(SESSION_REPLAY_CONFIG) private readonly config: SessionReplayConfig,
    @Inject(SESSION_REPLAY_RUNTIME) private readonly runtime: SessionReplayRuntime,
    @Inject(SESSION_REPLAY_RECORDER_LOADER) private readonly loadRecorder: RecorderLoader,
  ) {}

  async start(): Promise<void> {
    if (this.stopRecording || !canRecord(this.config, this.runtime.location.hostname)) {
      return;
    }
    this.session = this.resumeSession();
    this.qaCard = this.resolveQaCard();
    try {
      const startRecording = await this.loadRecorder();
      this.stopRecording = startRecording((event) => this.record(event), this.config.ingestUrl);
    } catch (error) {
      console.warn('[SessionReplay] recorder failed to start', error);
      return;
    }
    this.timer = this.runtime.setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    this.removePageHide = this.runtime.onPageHide(() => void this.flush(true));
  }

  stop(): void {
    this.stopRecording?.();
    this.stopRecording = undefined;
    if (this.timer !== undefined) {
      this.runtime.clearInterval(this.timer);
      this.timer = undefined;
    }
    this.removePageHide?.();
    this.removePageHide = undefined;
  }

  async flush(keepalive = false): Promise<void> {
    if (!this.session || this.buffer.length === 0) {
      return;
    }
    const events = this.buffer;
    this.buffer = [];
    this.bufferedChars = 0;
    const seq = this.session.seq;
    this.session = { ...this.session, seq: seq + 1 };
    this.runtime.storage.setItem(SESSION_KEY, JSON.stringify(this.session));

    const url = buildChunkUrl(this.config.ingestUrl, this.session.id, seq, this.session.startDate);
    const json = JSON.stringify({
      v: 1,
      sessionId: this.session.id,
      seq,
      startDate: this.session.startDate,
      meta: this.currentMeta(),
      events,
    });
    try {
      // A closing tab is torn down before an async gzip resolves, so the final upload starts in this
      // tick as plain text (CORS-safelisted: no preflight) and the intake compresses it instead.
      if (keepalive && json.length <= KEEPALIVE_MAX_BYTES) {
        await this.runtime.fetch(url, {
          method: 'POST',
          headers: { 'content-type': 'text/plain' },
          body: json,
          keepalive: true,
        });
        return;
      }
      await this.runtime.fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream' },
        body: await this.runtime.compress(json),
        keepalive: false,
      });
    } catch {
      // Recording is best effort: a lost chunk must never surface to the user.
    }
  }

  private record(event: ReplayEvent): void {
    const clean = sanitizeEvent(event);
    this.buffer.push(clean);
    this.bufferedChars += JSON.stringify(clean).length;
    // The page snapshot is the one batch too large for the tab-close path, so ship it while the page is alive.
    if (clean.type === FULL_SNAPSHOT_EVENT || this.bufferedChars >= FLUSH_AT_CHARS) {
      void this.flush();
    }
  }

  private currentMeta() {
    const user = this.profileService.getCurrentUser();
    return {
      userId: user?.id ?? null,
      orgId: user?.currentOrgId ?? this.runtime.storage.getItem('selectedOrgId') ?? null,
      qaCard: this.qaCard,
      path: stripUrl(this.runtime.location.href),
    };
  }

  private resumeSession(): StoredSession {
    const stored = this.runtime.storage.getItem(SESSION_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as StoredSession;
      } catch {
        // Corrupt entry: start a fresh session below.
      }
    }
    const session = { id: this.runtime.randomUUID(), startDate: this.runtime.today(), seq: 0 };
    this.runtime.storage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  }

  private resolveQaCard(): string | null {
    const { card, storage } = parseQaCard(this.runtime.location.search, this.runtime.storage.getItem(QA_CARD_KEY));
    if (storage === 'set' && card) {
      this.runtime.storage.setItem(QA_CARD_KEY, card);
    } else if (storage === 'remove') {
      this.runtime.storage.removeItem(QA_CARD_KEY);
    }
    return card;
  }
}
